import { __assign, __rest } from "tslib";
import React from 'react';
import { createFilter, } from 'react-select';
import { defaultProps as builtins } from 'react-select/creatable';
import clsx from 'clsx';
import { ClickAwayListener } from '../ClickAwayListener';
import { announceInputFocus, announceMenuInstructions, announceSearchResults, getOnChangeAriaLiveMessage, } from './PickerAccessibility';
import { OffScreenAnnouncement } from '../OffScreenAnnouncement';
import { Control, Menu, MultiValue, NoOptionsMessage, Option, Placeholder, SingleValue, ValueContainer, DropdownIndicator, ClearIndicator, IndicatorsContainer, IndicatorSeparator, } from './PickerComponents';
var defaultInternalComponents = {
    Control: Control,
    Menu: Menu,
    MultiValue: MultiValue,
    NoOptionsMessage: NoOptionsMessage,
    Option: Option,
    Placeholder: Placeholder,
    SingleValue: SingleValue,
    ValueContainer: ValueContainer,
    DropdownIndicator: DropdownIndicator,
    ClearIndicator: ClearIndicator,
    IndicatorsContainer: IndicatorsContainer,
    IndicatorSeparator: IndicatorSeparator,
};
// clear base styles
var selectStyles = {
    container: function () { return ({}); },
    input: function () { return ({}); },
    menu: function () { return ({}); },
};
function isGroupOptionType(options) {
    return options.length > 0 && !!options[0].options;
}
export var BasePicker = function (props) {
    var _a;
    var isCreatable = props.isCreatable, classes = props.classes, disabled = props.disabled, theme = props.theme, inputId = props.inputId, onKeyDown = props.onKeyDown, onChange = props.onChange, onInputChange = props.onInputChange, filterOption = props.filterOption, onFocus = props.onFocus, onBlur = props.onBlur, onMenuOpen = props.onMenuOpen, onMenuClose = props.onMenuClose, isValidNewOption = props.isValidNewOption, components = props.components, showOnlyFilteredResults = props.showOnlyFilteredResults, showOnlyFilteredMessage = props.showOnlyFilteredMessage, noOptionsMessage = props.noOptionsMessage, ChosenSelectComponent = props.ChosenSelectComponent, other = __rest(props, ["isCreatable", "classes", "disabled", "theme", "inputId", "onKeyDown", "onChange", "onInputChange", "filterOption", "onFocus", "onBlur", "onMenuOpen", "onMenuClose", "isValidNewOption", "components", "showOnlyFilteredResults", "showOnlyFilteredMessage", "noOptionsMessage", "ChosenSelectComponent"]);
    var containerEl = React.useRef(null);
    var selectEl = React.useRef(null);
    var _b = React.useState(''), ariaInputValue = _b[0], setAriaInputValue = _b[1];
    var _c = React.useState(false), isFocusVisible = _c[0], setFocusVisible = _c[1];
    var _d = React.useState(''), ariaLiveMessage = _d[0], setAriaLiveMessage = _d[1];
    var _e = React.useState(0), filteredOptions = _e[0], setFilteredOptions = _e[1];
    var _f = React.useState(false), ariaIsValidNewOption = _f[0], setAriaIsValidNewOption = _f[1];
    var filteredCount = React.useRef(filteredOptions);
    var muiTheme = theme;
    var isMounted = React.useRef(true);
    React.useEffect(function () {
        isMounted.current = true;
        if (filteredOptions === filteredCount.current) {
            setTimeout(function () {
                if (ariaInputValue && isMounted.current) {
                    setAriaLiveMessage("\n            ".concat(announceSearchResults(props, ariaInputValue, filteredCount.current, ariaIsValidNewOption), "\n            ").concat(!props.isCreatable && filteredCount.current > 1 ? announceMenuInstructions(props) : '', "\n          "));
                }
            });
        }
        return function () {
            isMounted.current = false;
        };
    }, [ariaInputValue, ariaIsValidNewOption, filteredOptions, props]);
    function extractAllOptions(options) {
        if (isGroupOptionType(options)) {
            return options.reduce(function (acc, group) { return acc.concat(group.options); }, []);
        }
        return options;
    }
    function handleIsValidNewOption(newOption, value, options) {
        var isValid = false;
        var allOptions = extractAllOptions(options);
        if (isValidNewOption) {
            isValid = isValidNewOption(newOption, value, allOptions);
        }
        else {
            // If the user did not provide their own `isValidNewOption` callback, use the one from react-select.
            if (builtins.isValidNewOption) {
                isValid = builtins.isValidNewOption(newOption, value, allOptions);
            }
            else {
                throw new Error('"isValidNewOption" was not provided as a prop and does not exist in react-select builtins (was it removed in a new version?)');
            }
        }
        setAriaIsValidNewOption(isValid);
        return isValid;
    }
    function handleKeyDown(event) {
        if (!isFocusVisible) {
            setFocusVisible(true);
        }
        if (onKeyDown) {
            // Declaring as any to fix a type conflict -- react-transition-group in
            // the Picker chain is using a mismatched version of @types/react.
            onKeyDown(event);
        }
    }
    function handleOnChange(value, action) {
        var newAriaLiveMessage = getOnChangeAriaLiveMessage(props, value, action);
        if (newAriaLiveMessage !== null) {
            setAriaLiveMessage(newAriaLiveMessage);
        }
        if (onChange) {
            onChange(value, action);
        }
    }
    function handleOnInputChange(value, action) {
        if (action.action === 'input-change') {
            setAriaInputValue(value);
            resetFilterCount();
        }
        if (onInputChange) {
            onInputChange(value, action);
        }
    }
    function handleOnFocus(event) {
        setAriaLiveMessage(announceInputFocus(props));
        if (onFocus) {
            onFocus(event);
        }
    }
    function handleOnBlur(event) {
        setAriaLiveMessage('');
        if (onBlur) {
            onBlur(event);
        }
    }
    function handleOnMenuOpen() {
        setAriaLiveMessage(announceMenuInstructions(props));
        if (onMenuOpen) {
            onMenuOpen();
        }
    }
    function handleOnMenuClose() {
        setFocusVisible(false);
        setAriaLiveMessage(announceInputFocus(props));
        if (onMenuClose) {
            onMenuClose();
        }
    }
    function filterOptions(candidate, input) {
        var filter = filterOption || createFilter({});
        if (input) {
            var filtered = filter(candidate, input);
            if (filtered && !props.isCreatable) {
                filteredCount.current += 1;
                setTimeout(function () {
                    if (!isMounted.current)
                        return;
                    setFilteredOptions(filteredCount.current);
                });
            }
            return filtered;
        }
        return !showOnlyFilteredResults;
    }
    function resetFilterCount() {
        filteredCount.current = 0;
        setFilteredOptions(0);
    }
    function getNoOptionsMessage(input) {
        var message = showOnlyFilteredResults && !input.inputValue ? showOnlyFilteredMessage : noOptionsMessage;
        return (message && message(input)) || null;
    }
    var mergedComponents = components
        ? __assign(__assign({}, defaultInternalComponents), components) : defaultInternalComponents;
    return (React.createElement(React.Fragment, null,
        React.createElement(ClickAwayListener, { onClickAway: function () { return setFocusVisible(false); } },
            React.createElement(ChosenSelectComponent, __assign({ labelId: "".concat(inputId, "-label"), filterOption: filterOptions, isCreatable: isCreatable, classes: classes, className: clsx(classes.root, (_a = {}, _a[classes.focusVisible] = isFocusVisible, _a)), components: mergedComponents, styles: selectStyles, inputId: inputId, ref: selectEl, containerRef: containerEl, selectRef: selectEl, isDisabled: disabled, isRtl: muiTheme.direction === 'rtl', escapeClearsValue: true, setAriaLiveMessage: setAriaLiveMessage, onKeyDown: handleKeyDown, onChange: handleOnChange, onInputChange: handleOnInputChange, onFocus: handleOnFocus, onBlur: handleOnBlur, onMenuOpen: handleOnMenuOpen, onMenuClose: handleOnMenuClose, isValidNewOption: handleIsValidNewOption, noOptionsMessage: getNoOptionsMessage }, other))),
        React.createElement(OffScreenAnnouncement, { id: "".concat(inputId, "-live-region"), message: ariaLiveMessage })));
};
export var basePickerDefaultProps = {
    isSearchable: true,
    screenReaderStatus: function (_a) {
        var count = _a.count;
        return "".concat(count, " result").concat(count !== 1 ? 's' : '', " available.");
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    noOptionsMessage: function (_a) {
        var inputValue = _a.inputValue;
        return 'No tags found.';
    },
    showOnlyFilteredMessage: function () { return 'Search for available options.'; },
    placeholder: '',
    clearInputAriaLabel: 'Remove selected options.',
    removeTagAriaLabel: function (_a) {
        var label = _a.label;
        return "Deselect option ".concat(label, ".");
    },
    ariaLiveMessages: {
        inputFocus: function (_a) {
            var label = _a.label;
            return "".concat(label, " is focused, press Down to open the menu.");
        },
        inputFocusIsSearchable: 'Type to refine list.',
        inputFocusIsMulti: 'Press Left to focus selected values.',
        tagInstructions: 'Use left and right to toggle between focused values, press Backspace to remove the currently focused value.',
        tagFocus: function (_a) {
            var label = _a.label, current = _a.current, total = _a.total;
            return "Value ".concat(label, " focused, ").concat(current, " of ").concat(total, ".");
        },
        tagRemove: function (_a) {
            var label = _a.label;
            return "Option ".concat(label, ", deselected.");
        },
        tagCreate: function (_a) {
            var label = _a.label;
            return "Option ".concat(label, ", created.");
        },
        menuInstructions: 'Use Up and Down to navigate options, press Enter to select the currently focused option, press Escape to exit the menu.',
        optionFocus: function (_a) {
            var label = _a.label, current = _a.current, total = _a.total;
            return "Option ".concat(label, " focused, ").concat(current, " of ").concat(total, ".");
        },
        optionSelect: function (_a) {
            var label = _a.label;
            return "Option ".concat(label, ", selected.");
        },
        optionsAvailable: function (_a) {
            var count = _a.count;
            return "".concat(count, " result").concat(count !== 1 ? 's' : '', " available.");
        },
        searchResults: function (_a) {
            var count = _a.count, value = _a.value;
            return "".concat(count, " result").concat(count !== 1 ? 's' : '', " available for search term ").concat(value, ".");
        },
        searchResultsIsCreatable: function (_a) {
            var count = _a.count, value = _a.value;
            return "".concat(count, " result").concat(count !== 1 ? 's' : '', " available for search term ").concat(value, ". ").concat(count === 1 ? 'Press Enter to create a new option.' : '');
        },
    },
    highlightFilteredMatches: true,
};
//# sourceMappingURL=BasePicker.js.map